import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuthUser, useIsAuthenticated } from "@/hooks/auth/useAuth";
import { useTenant } from "../../contexts/TenantContext";
import { savedPropertyService } from "../../api/savedProperties";
import { analyticsService } from "../../services/analyticsService";
import { BookmarkPlus, Check, Loader } from "lucide-react";

interface SavePropertyButtonProps {
  mlsNumber: string;
  propertyId?: string; // Add propertyId parameter
  mlsBoardCode?: string; // Add mlsBoardCode parameter
  address: {
    full: string;
    city: string;
    state: string;
    zip: string;
  };
  details?: {
    bedrooms?: number;
    bathrooms?: number;
    squareFeet?: number;
    price?: number;
    listprice?: string;
    listpricelow?: string;
    internetAddressDisplayYN?: boolean;
    mustRemovePrimaryPhoto?: boolean;
    primaryPhotoUrl?: string;
    features?: string[];
    listingStatus?: string;
    listingOffice?: string;
    mlsstatus?: string;
  };
  onSaved?: () => void;
  className?: string;
}

export const SavePropertyButton: React.FC<SavePropertyButtonProps> = ({
  mlsNumber,
  propertyId,
  mlsBoardCode,
  address,
  details,
  onSaved,
  className = "",
}) => {
  const user = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { config } = useTenant();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedPropertyId, setSavedPropertyId] = useState<string | null>(null);

  // Check if property is already saved on mount
  useEffect(() => {
    const checkIfSaved = async () => {
      if (!isAuthenticated || !user || !config?.id) return;

      try {
        const savedProperty = await savedPropertyService.findSavedProperty(
          user.id,
          config.id,
          mlsNumber
        );
        if (savedProperty) {
          setIsSaved(true);
          setSavedPropertyId(savedProperty.id);
        }
      } catch (error) {
        console.error("Error checking saved status:", error);
      }
    };

    checkIfSaved();
  }, [isAuthenticated, user, mlsNumber, config?.id]);

  const primaryColor = config?.branding?.colors?.primary || "#000000";

  const handleClick = async () => {
    if (!isAuthenticated || !user) {
      // Redirect to login with property data and return URL
      navigate("/auth/login", {
        state: {
          from: window.location.pathname,
          propertyToSave: { mlsNumber, address, details },
        },
      });
      return;
    }

    if (!config?.id) {
      console.error("Organization ID not found in config");
      return;
    }

    setIsLoading(true);
    try {
      if (!user.id) {
        throw new Error("Contact ID not found");
      }

      if (isSaved && savedPropertyId) {
        // Unsave the property
        const result = await savedPropertyService.removeSavedProperty(
          savedPropertyId
        );
        if (result.success) {
          setIsSaved(false);
          setSavedPropertyId(null);
          queryClient.invalidateQueries({ queryKey: ["savedProperties"] });

          onSaved?.();
        }
      } else {
        // Save the property
        const result = await savedPropertyService.saveProperty(
          user.id,
          config.id,
          propertyId || mlsNumber, // Use propertyId if available, otherwise fall back to mlsNumber
          mlsNumber,
          mlsBoardCode, // Pass mlsBoardCode to savedPropertyService
          address,
          details
        );

        if (result.success && result.property) {
          setIsSaved(true);
          setSavedPropertyId(result.property.id);
          queryClient.invalidateQueries({ queryKey: ["savedProperties"] });

          // Track property save event
          analyticsService.trackEvent({
            tenantId: config?.id,
            orgId: config?.orgData?.id,
            eventType: "interaction",
            category: "Property",
            action: "Save Property",
            label: address.full,
            data: {
              propertyId: propertyId || mlsNumber,
              mlsNumber,
              mlsBoardCode,
              address: address.full,
              city: address.city,
              state: address.state,
              zipCode: address.zip,
              bedrooms: details?.bedrooms,
              bathrooms: details?.bathrooms,
              squareFeet: details?.squareFeet,
              price: details?.price,
            },
          });

          onSaved?.();
        }
      }
    } catch (error) {
      console.error("Error handling property save/unsave:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`inline-flex items-center px-4 py-2 rounded-lg text-xs 2xl:text-sm font-medium
        bg-white hover:bg-gray-50 border
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-200
        ${className}`}
      style={{
        color: isSaved ? "#16a34a" : primaryColor,
        borderColor: isSaved ? "#16a34a" : primaryColor,
      }}
    >
      {isLoading ? (
        <>
          <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
          Loading...
        </>
      ) : isSaved ? (
        <>
          <Check className="-ml-1 mr-2 h-4 w-4" />
          Saved
        </>
      ) : (
        <>
          <BookmarkPlus className="-ml-1 mr-2 h-4 w-4" />
          {isAuthenticated ? "Save Property" : "Login to Save"}
        </>
      )}
    </button>
  );
};
